import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useTranslation } from 'react-i18next';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';

interface StakingRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart?: () => void;
}

type SlideTitleKey = 'slide1.title' | 'slide2.title' | 'slide3.title' | 'slide4.title' | 'slide5.title' | 'slide6.title';
type SlideDescKey = 'slide1.description' | 'slide2.description' | 'slide3.description' | 'slide4.description' | 'slide5.description' | 'slide6.description';

interface SlideItem {
  id: string;
  titleKey: SlideTitleKey;
  descriptionKey: SlideDescKey;
  gradient: string;
  emojiId: string;
  glowColor: string;
}

const slides: SlideItem[] = [
  {
    id: 'nft_staking',
    titleKey: 'slide1.title',
    descriptionKey: 'slide1.description',
    gradient: 'from-[#2ecc71]/25 via-[#27ae60]/10 to-[#0d1f14]',
    emojiId: '105',
    glowColor: '#2ecc71',
  },
  {
    id: 'gram_yield',
    titleKey: 'slide2.title',
    descriptionKey: 'slide2.description',
    gradient: 'from-[#0284c7]/25 via-[#0369a1]/10 to-[#071927]',
    emojiId: '98',
    glowColor: '#38bdf8',
  },
  {
    id: 'ownership_bots',
    titleKey: 'slide3.title',
    descriptionKey: 'slide3.description',
    gradient: 'from-[#9333ea]/25 via-[#7e22ce]/10 to-[#190b29]',
    emojiId: '110',
    glowColor: '#c084fc',
  },
  {
    id: 'p2p_market',
    titleKey: 'slide4.title',
    descriptionKey: 'slide4.description',
    gradient: 'from-[#d97706]/25 via-[#b45309]/10 to-[#231405]',
    emojiId: '107',
    glowColor: '#fbbf24',
  },
  {
    id: 'zero_lock',
    titleKey: 'slide5.title',
    descriptionKey: 'slide5.description',
    gradient: 'from-[#059669]/25 via-[#047857]/10 to-[#091f17]',
    emojiId: '108',
    glowColor: '#34d399',
  },
  {
    id: 'multipliers_boost',
    titleKey: 'slide6.title',
    descriptionKey: 'slide6.description',
    gradient: 'from-[#e11d48]/25 via-[#be123c]/10 to-[#270b13]',
    emojiId: 'ch1',
    glowColor: '#fb7185',
  }
];

export const StakingRulesModal: React.FC<StakingRulesModalProps> = ({
  isOpen,
  onClose,
  onStart
}) => {
  const { impactOccurred } = useHapticFeedback();
  const { t } = useTranslation('rules');
  const [activeSlide, setActiveSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [direction, setDirection] = useState(1);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setActiveSlide(0);
      setProgress(0);
      return;
    }

    const interval = 40;
    const step = 100 / (4000 / interval);

    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setDirection(1);
          setActiveSlide((current) => (current + 1) % slides.length);
          return 0;
        }
        return prev + step;
      });
    }, interval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, activeSlide]);

  const handleNext = () => {
    impactOccurred('light');
    if (activeSlide < slides.length - 1) {
      setDirection(1);
      setActiveSlide(activeSlide + 1);
      setProgress(0);
    } else {
      if (onStart) onStart();
      onClose();
    }
  };

  const handleDotClick = (index: number) => {
    impactOccurred('light');
    setDirection(index > activeSlide ? 1 : -1);
    setActiveSlide(index);
    setProgress(0);
  };

  const handleDragEnd = (_: any, info: any) => {
    // Ultra-responsive threshold: short drag down (> 20px or velocity > 50) triggers instant 0.18s dismissal
    if (info.offset.y > 20 || info.velocity.y > 50) {
      impactOccurred('light');
      onClose();
    }
  };

  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 80 : -80,
      opacity: 0
    })
  };

  if (!isOpen) return null;

  const currentItem = slides[activeSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-xl flex items-end sm:items-center justify-center p-0 sm:p-4">
          {/* Backdrop Click to Close */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            onClick={onClose}
            className="absolute inset-0 z-0"
          />

          {/* Modal Bottom Sheet Container */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%', transition: { duration: 0.18, ease: [0.32, 0, 0.67, 0] } }}
            transition={{ type: 'spring', damping: 24, stiffness: 420, mass: 0.4 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 1 }}
            dragSnapToOrigin={true}
            onDragEnd={handleDragEnd}
            className="w-full max-w-md bg-[#1c1c1e] rounded-t-[36px] sm:rounded-[36px] px-4 pt-3 pb-6 shadow-2xl relative z-10 overflow-hidden flex flex-col items-center touch-pan-y"
          >
            {/* Top Grab Bar Handle Pill (Swipable down & Clickable) */}
            <div
              onClick={() => { impactOccurred('light'); onClose(); }}
              className="w-12 h-1.5 rounded-full bg-white/20 mx-auto mb-3 cursor-pointer hover:bg-white/40 active:scale-95 transition-all"
            />

            {/* ─── Top Hero Graphics Card ─── */}
            <div className="w-full h-64 rounded-[28px] relative overflow-hidden mb-6 shadow-inner flex items-center justify-center">
              <motion.div
                key={currentItem.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35 }}
                className={`absolute inset-0 bg-gradient-to-b ${currentItem.gradient} flex items-center justify-center`}
              >
                {/* Background Subtle Sparks/Grid */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.18)_0,transparent_70%)] pointer-events-none" />

                {/* Center 60 FPS Custom Animated Emoji */}
                <div className="relative z-10 flex items-center justify-center filter drop-shadow-2xl">
                  <CustomEmojiPlayer
                    emojiId={currentItem.emojiId}
                    size={140}
                    glowColor={currentItem.glowColor}
                  />
                </div>
              </motion.div>
            </div>

            {/* ─── Slide Title & Description ─── */}
            <div className="min-h-[90px] flex flex-col items-center justify-center text-center mb-5 px-4 w-full">
              <AnimatePresence custom={direction} mode="wait">
                <motion.div
                  key={currentItem.id}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.25 }}
                  className="flex flex-col items-center w-full"
                >
                  <h2 className="text-2xl font-bold text-white tracking-tight mb-2">
                    {t(currentItem.titleKey)}
                  </h2>
                  <p className="text-[13px] text-[#8e8e93] font-normal leading-relaxed max-w-[285px] text-center">
                    {t(currentItem.descriptionKey)}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* ─── Animated Capsule Progress Dots Bar (كبسولة ممتلئة متمددة) ─── */}
            <div className="flex items-center justify-center gap-1.5 mb-6">
              {slides.map((slide, index) => {
                const isActive = index === activeSlide;
                return (
                  <div
                    key={slide.id}
                    onClick={() => handleDotClick(index)}
                    className={`h-1.5 rounded-full cursor-pointer transition-all duration-300 relative overflow-hidden ${
                      isActive ? 'w-5 bg-white/20' : 'w-1.5 bg-white/20 hover:bg-white/40'
                    }`}
                  >
                    {/* Animated Progress Fill Bar inside active capsule */}
                    {isActive && (
                      <motion.div
                        className="h-full bg-white rounded-full"
                        style={{ width: `${progress}%` }}
                        transition={{ ease: 'linear' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* ─── Bottom Action Button ("Continue" Pill) ─── */}
            <button
              onClick={handleNext}
              className="w-full h-13 py-3.5 rounded-full bg-[#2c2c2e] hover:bg-[#3a3a3c] active:scale-[0.98] text-white font-semibold text-sm tracking-wide transition-all flex items-center justify-center shadow-lg"
            >
              <span>{t('continue')}</span>
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StakingRulesModal;
